import { useState, useRef, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  IconButton,
  Paper,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Link
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import PersonIcon from '@mui/icons-material/Person'

// API Gateway URL with rate limiting (50 req/sec sustained, 100 burst)
const API_URL = 'https://nml48dqn7h.execute-api.us-east-1.amazonaws.com/prod/'

const welcomeMessage = "I'm an AI assistant trained on Powertron's PermaFrost NMR corpus data. I can answer questions about HVAC efficiency, case studies, and the technology. What would you like to know?"

const suggestedQuestions = [
  "What do engineers get wrong about this technology?",
  "What IPMVP protocols validated the results?",
  "How many PE-certified studies support this?",
  "What causes HVAC systems to lose efficiency over time?",
  "What kW/ton improvements were measured?",
  "Which independent labs tested the technology?"
]

const exampleQA = [
  {
    q: "What is the average efficiency improvement?",
    a: "Based on 134 PE-certified field studies, the average efficiency improvement is 23.2% with a range from 3.8% to 61%. The median is 22.1%.",
    sources: ["CS_001", "CS_023"]
  },
  {
    q: "How long does payback typically take?",
    a: "Payback periods range from 7–16 months across all documented case studies, with an average savings of $19,261 per system annually.",
    sources: ["CS_007", "CS_045"]
  },
  {
    q: "Which independent labs tested the technology?",
    a: "Six independent laboratories verified PermaFrost NMR, including UL Laboratories (DERA report) and Texas OilTech Labs. All testing followed ASHRAE 37 protocols with PE-certified measurements.",
    sources: ["CS_012", "CS_089"]
  }
]

function AIAssistant() {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: welcomeMessage,
      timestamp: new Date()
    }
  ])
  const [input, setInput] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [error, setError] = useState(null)
  const messagesEndRef = useRef(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const handleSend = async () => {
    if (!input.trim()) return

    const userMessage = {
      role: 'user',
      content: input,
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    setInput('')
    setIsTyping(true)
    setError(null)

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: input })
      })

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`)
      }

      const data = await response.json()

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        sources: data.sources || [],
        timestamp: new Date()
      }])
    } catch (err) {
      console.error('Chat error:', err)
      setError('Unable to connect to the AI assistant. Please try again.')
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Sorry, I encountered an error connecting to the knowledge base. Please try again in a moment.",
        timestamp: new Date()
      }])
    }

    setIsTyping(false)
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleSuggestion = (question) => {
    setInput(question)
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', height: 'calc(100vh - 140px)', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
        Ask AI
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Ask questions about PermaFrost NMR technology, case studies, and HVAC efficiency.
        <Chip
          label="Live"
          size="small"
          sx={{ ml: 1 }}
          color="success"
        />
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Suggested Questions */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {suggestedQuestions.map((q, i) => (
          <Chip
            key={i}
            label={q}
            size="small"
            variant="outlined"
            onClick={() => handleSuggestion(q)}
            sx={{ cursor: 'pointer' }}
          />
        ))}
      </Box>

      {/* Chat Messages */}
      <Card sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flex: 1, overflow: 'auto', p: { xs: 2, sm: 3 } }}>
          {messages.map((msg, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                gap: 1.5,
                mb: 2,
                flexDirection: msg.role === 'user' ? 'row-reverse' : 'row'
              }}
            >
              <Avatar
                sx={{
                  bgcolor: msg.role === 'user' ? 'primary.main' : 'secondary.main',
                  width: 36,
                  height: 36
                }}
              >
                {msg.role === 'user' ? <PersonIcon /> : <SmartToyIcon />}
              </Avatar>
              <Box sx={{ maxWidth: '70%' }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: msg.role === 'user' ? 'primary.main' : 'background.paper',
                    color: msg.role === 'user' ? 'white' : 'text.primary',
                    borderRadius: 2,
                    border: msg.role === 'assistant' ? '1px solid' : 'none',
                    borderColor: 'divider'
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {msg.content}
                  </Typography>
                </Paper>
                {/* Display source citations */}
                {msg.sources && msg.sources.length > 0 && (
                  <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                      Sources:
                    </Typography>
                    {msg.sources.map((source, i) => (
                      <Link
                        key={i}
                        href={`/case-studies/${source}`}
                        underline="hover"
                        sx={{ fontSize: '0.75rem' }}
                      >
                        <Chip
                          label={source}
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' }
                          }}
                        />
                      </Link>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          ))}

          {/* Example Q&A shown before first user interaction */}
          {messages.length === 1 && !isTyping && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="overline" sx={{ color: 'text.disabled', letterSpacing: '0.1em', display: 'block', mb: 1.5 }}>
                Example questions & answers
              </Typography>
              {exampleQA.map((qa, i) => (
                <Box key={i} sx={{ mb: 2.5, opacity: 0.75 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, mb: 1, flexDirection: 'row-reverse' }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 28, height: 28 }}><PersonIcon sx={{ fontSize: 16 }} /></Avatar>
                    <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'primary.main', color: 'white', borderRadius: 2 }}>
                      <Typography variant="body2">{qa.q}</Typography>
                    </Paper>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: 'secondary.main', width: 28, height: 28 }}><SmartToyIcon sx={{ fontSize: 16 }} /></Avatar>
                    <Box>
                      <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body2">{qa.a}</Typography>
                      </Paper>
                      <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5 }}>
                        <Typography variant="caption" color="text.disabled">Sources:</Typography>
                        {qa.sources.map((s) => (
                          <Chip key={s} label={s} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {isTyping && (
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
              <Avatar sx={{ bgcolor: 'secondary.main', width: 36, height: 36 }}>
                <SmartToyIcon />
              </Avatar>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <CircularProgress size={20} />
              </Paper>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </CardContent>

        {/* Input */}
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              placeholder="Ask about PermaFrost NMR..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              multiline
              maxRows={3}
              size="small"
            />
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={!input.trim() || isTyping}
              aria-label="Send message"
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Box>
      </Card>
    </Box>
  )
}

export default AIAssistant
